import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, createUser, updateUser, deleteUser } from '../store/slices/usersSlice';
import { fetchMachines } from '../store/slices/machinesSlice';

const RoleBadge = ({ role }) => {
  const roleColors = {
    admin: 'danger',
    operator: 'primary',
    viewer: 'secondary',
    warehouse: 'info'
  };

  const roleLabels = {
    admin: 'Administrator',
    operator: 'Operator',
    viewer: 'Viewer',
    warehouse: 'Magazijn'
  };

  return (
    <span className={`badge bg-${roleColors[role] || 'secondary'}`}>
      {roleLabels[role] || role}
    </span>
  );
};

const Users = () => {
  const dispatch = useDispatch();
  const { users, pagination, loading, error } = useSelector((state) => state.users);
  const { machines } = useSelector((state) => state.machines);
  const { user: currentAuthUser } = useSelector((state) => state.auth);

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer',
    assignedMachines: [],
    isActive: true
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchMachines());
  }, [dispatch]);

  const roles = [
    { value: 'admin', label: 'Administrator', description: 'Volledige toegang tot alle functies' },
    { value: 'operator', label: 'Operator', description: 'Kan orders en machines beheren' },
    { value: 'viewer', label: 'Viewer', description: 'Alleen bekijken, geen wijzigingen' },
    { value: 'warehouse', label: 'Magazijn', description: 'Toegang tot scan en magazijn functies' }
  ];

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'viewer',
      assignedMachines: [],
      isActive: true
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      assignedMachines: user.assignedMachines?.map(m => m._id || m) || [],
      isActive: user.isActive
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name || !formData.email) {
      setFormError('Naam en e-mail zijn verplicht');
      return;
    }

    if (!editingUser && !formData.password) {
      setFormError('Wachtwoord is verplicht voor nieuwe gebruikers');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setFormError('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          assignedMachines: formData.assignedMachines,
          isActive: formData.isActive
        };
        await dispatch(updateUser({ id: editingUser._id, data: updateData })).unwrap();
      } else {
        await dispatch(createUser(formData)).unwrap();
      }
      setShowModal(false);
      dispatch(fetchUsers());
    } catch (err) {
      setFormError(err || 'Er is een fout opgetreden');
    }
  };

  const handleDelete = async (user) => {
    if (user._id === currentAuthUser?._id) {
      alert('Je kunt jezelf niet verwijderen');
      return;
    }

    if (window.confirm(`Weet je zeker dat je ${user.name} wilt verwijderen?`)) {
      try {
        await dispatch(deleteUser(user._id)).unwrap();
      } catch (err) {
        alert(err || 'Fout bij verwijderen');
      }
    }
  };

  const toggleMachine = (machineId) => {
    if (formData.assignedMachines.includes(machineId)) {
      setFormData({
        ...formData,
        assignedMachines: formData.assignedMachines.filter(id => id !== machineId)
      });
    } else {
      setFormData({
        ...formData,
        assignedMachines: [...formData.assignedMachines, machineId]
      });
    }
  };

  return (
    <div className="users-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Gebruikers</h1>
          <p className="text-muted">Beheer gebruikers, rollen en machine toegang</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-2"></i>
          Nieuwe gebruiker
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : !users || users.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-3"></i>
              <p>Geen gebruikers gevonden</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Naam</th>
                    <th>E-mail</th>
                    <th>Rol</th>
                    <th>Machines</th>
                    <th>Status</th>
                    <th>Laatste login</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <div className="fw-medium">{user.name}</div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <RoleBadge role={user.role} />
                      </td>
                      <td>
                        {user.assignedMachines?.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {user.assignedMachines.slice(0, 2).map((machine) => (
                              <span key={machine._id || machine} className="badge bg-light text-dark">
                                {machine.name || machine}
                              </span>
                            ))}
                            {user.assignedMachines.length > 2 && (
                              <span className="badge bg-secondary">
                                +{user.assignedMachines.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted small">Alle machines</span>
                        )}
                      </td>
                      <td>
                        {user.isActive ? (
                          <span className="badge bg-success">Actief</span>
                        ) : (
                          <span className="badge bg-secondary">Inactief</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString('nl-NL')
                          : '-'}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => openEditModal(user)}
                            title="Bewerken"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(user)}
                            disabled={user._id === currentAuthUser?._id}
                            title="Verwijderen"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingUser ? 'Gebruiker bewerken' : 'Nieuwe gebruiker'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    <div className="alert alert-danger">{formError}</div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Naam *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">E-mail *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Wachtwoord {editingUser ? '(leeg laten om niet te wijzigen)' : '*'}
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder={editingUser ? 'Niet wijzigen' : 'Minimaal 6 tekens'}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Rol *</label>
                        <select
                          className="form-select"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        >
                          {roles.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">
                          {roles.find(r => r.value === formData.role)?.description}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Gebruiker is actief
                      </label>
                    </div>
                  </div>

                  <hr />

                  <div className="mb-3">
                    <label className="form-label fw-medium">Toegewezen machines</label>
                    <p className="text-muted small mb-3">
                      Selecteer de machines waar deze gebruiker toegang toe heeft.
                      Laat leeg voor toegang tot alle machines (voor admin/operator).
                    </p>
                    <div className="row">
                      {machines.map((machine) => (
                        <div key={machine._id} className="col-md-6 col-lg-4 mb-2">
                          <div
                            className={`card cursor-pointer ${formData.assignedMachines.includes(machine._id) ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                            onClick={() => toggleMachine(machine._id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="card-body py-2 px-3">
                              <div className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2"
                                  checked={formData.assignedMachines.includes(machine._id)}
                                  onChange={() => {}}
                                />
                                <div>
                                  <div className="fw-medium small">{machine.name}</div>
                                  <small className="text-muted">{machine.type}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.assignedMachines.length === 0 && (
                      <div className="alert alert-info mt-2 mb-0 py-2">
                        <i className="bi bi-info-circle me-2"></i>
                        Geen machines geselecteerd - gebruiker heeft toegang tot alle machines
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Annuleren
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? 'Opslaan' : 'Aanmaken'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
